import { FilterList } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { StyledFooterCell, StyledHeaderCell } from '../../../styles/WorklogTableStyles'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  useGetUpcomingVacationsQuery,
  useGroupAcknowledgeVacationsMutation,
} from '../../../store/redux/reducers/upcomingVacationApiSlice'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { UpcomingVacationDTO } from '../../../store/types/utils/VacationTypes'
import FilterUpcomingVacationsPopup from '../../FilterMenu/FilterUpcomingVacationsPopup'
import UpcomingVacationRow from './UpcomingVacationRow'

const getDefaultFilter = () => {
  const today = dayjs()
  return {
    startDate: today.format('YYYY-MM-DD'),
    endDate: today.add(7, 'day').format('YYYY-MM-DD'),
    page: 0,
    size: 10,
    sortField: 'displayStartDate',
    sortOrder: 0,
    projects: [] as string[],
    acknowledged: 'all',
  }
}

const UpcomingVacationsTable: React.FC = () => {
  const theme = useTheme()
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const currentUser = useSelector(getCurrentUser)

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs())
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(7, 'day'))
  const [filters, setFilters] = useState<Record<string, any>>(getDefaultFilter())
  const [openFilter, setOpenFilter] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [editMode, setEditMode] = useState(false)
  const { data, isLoading } = useGetUpcomingVacationsQuery(filters)
  const [groupAcknowledgeVacations, { isLoading: isSaving }] = useGroupAcknowledgeVacationsMutation()
  const [checkedVacations, setCheckedVacations] = useState<string[]>([])
  const [initialChecked, setInitialChecked] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    if (startDate && endDate) {
      setFilters(prev => ({
        ...prev,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      }))
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (data?.content) {
      const preChecked = data.content
        .filter(v => Array.isArray(v.acknowledgedBy) && v.acknowledgedBy.length > 0)
        .map(v => v.vacationId)
      setCheckedVacations(preChecked)
      setInitialChecked(preChecked)
    }
  }, [data])

  const uniqueVacations = useMemo(() => {
    if (data?.content) {
      const map = new Map<string, UpcomingVacationDTO>()
      data.content.forEach(vac => {
        if (!map.has(vac.vacationId)) {
          map.set(vac.vacationId, vac)
        }
      })
      return Array.from(map.values())
    }
    return []
  }, [data])

  const handleCheck = (vacationId: string) => {
    if (checkedVacations.includes(vacationId)) {
      setCheckedVacations(prev => prev.filter(id => id !== vacationId))
    } else {
      setCheckedVacations(prev => [...prev, vacationId])
    }
  }

  const handleConfirmOrSave = async () => {
    if (!editMode) {
      setEditMode(true)
    } else {
      const changedIds = [
        ...checkedVacations.filter(id => !initialChecked.includes(id)),
        ...initialChecked.filter(id => !checkedVacations.includes(id)),
      ]
      if (changedIds.length === 0) {
        setEditMode(false)
        return
      }
      try {
        await groupAcknowledgeVacations(changedIds).unwrap()
        setInitialChecked(checkedVacations)
      } catch {}
      setEditMode(false)
    }
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10)
    setRowsPerPage(newSize)
    setPage(0)
    setFilters(prev => ({ ...prev, page: 0, size: newSize }))
  }

  const handleSort = (column: string) => {
    let newOrder = 0
    if (filters.sortField === column) {
      newOrder = filters.sortOrder === 0 ? 1 : 0
    }
    setFilters(prev => ({ ...prev, sortField: column, sortOrder: filters.sortField === column ? newOrder : 0 }))
  }

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setOpenFilter(prev => !prev)
  }

  const handleApplyFilters = (appliedFilters: {
    projects?: string[]
    startDate: string
    endDate: string
    acknowledged: string
  }) => {
    setFilters(prev => ({
      ...prev,
      projects: appliedFilters.projects,
      startDate: appliedFilters.startDate,
      endDate: appliedFilters.endDate,
      acknowledged: appliedFilters.acknowledged,
      page: 0,
    }))
  }

  const handleClearFilters = () => {
    setFilters(getDefaultFilter())
  }

  if (isLoading) return <Typography>{t('loading')}</Typography>

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}
      >
        <Typography variant="h6" sx={{ width: '100%' }}>
          {t('components.upcomingVacation.title')}
        </Typography>
        <Box display="flex" justifyContent="end" gap={1}>
          {isDownMd ? (
            <IconButton
              color="primary"
              onClick={handleToggleFilter}
              aria-label={t('components.upcomingVacation.filter.button')}
            >
              <FilterList />
            </IconButton>
          ) : (
            <Button startIcon={<FilterList />} onClick={handleToggleFilter}>
              {t('components.upcomingVacation.filter.button')}
            </Button>
          )}
          <Button variant="contained" onClick={handleConfirmOrSave} disabled={isSaving}>
            {editMode ? t('components.upcomingVacation.save') : t('components.upcomingVacation.confirmVacations')}
          </Button>
        </Box>
      </Box>
      <FilterUpcomingVacationsPopup
        open={openFilter}
        setOpen={setOpenFilter}
        anchorEl={anchorEl}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
      <TableContainer
        sx={{ width: '100%', overflowX: 'auto', border: '1px solid', borderColor: 'background.elevation8' }}
      >
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '60px' }} />
            <col style={{ width: 'auto', minWidth: '160px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '130px' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <StyledHeaderCell />
              <StyledHeaderCell>
                <TableSortLabel
                  active={filters.sortField === 'employee.firstName'}
                  direction={
                    filters.sortField === 'employee.firstName' ? (filters.sortOrder === 0 ? 'asc' : 'desc') : 'asc'
                  }
                  onClick={() => handleSort('employee.firstName')}
                >
                  {t('components.upcomingVacation.employee')}
                </TableSortLabel>
              </StyledHeaderCell>
              <StyledHeaderCell>
                <TableSortLabel
                  active={filters.sortField === 'displayStartDate'}
                  direction={
                    filters.sortField === 'displayStartDate' ? (filters.sortOrder === 0 ? 'asc' : 'desc') : 'asc'
                  }
                  onClick={() => handleSort('displayStartDate')}
                >
                  {t('components.upcomingVacation.timeSlot')}
                </TableSortLabel>
              </StyledHeaderCell>
              <StyledHeaderCell align="center">
                <TableSortLabel
                  active={filters.sortField === 'lastAcknowledgedDate'}
                  direction={
                    filters.sortField === 'lastAcknowledgedDate' ? (filters.sortOrder === 0 ? 'asc' : 'desc') : 'asc'
                  }
                  onClick={() => handleSort('lastAcknowledgedDate')}
                >
                  {t('components.upcomingVacation.ack')}
                </TableSortLabel>
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueVacations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  {t('components.upcomingVacation.empty')}
                </TableCell>
              </TableRow>
            ) : (
              uniqueVacations.map(vac => {
                const isChecked = checkedVacations.includes(vac.vacationId)
                return (
                  <UpcomingVacationRow
                    key={vac.vacationId}
                    vacation={vac}
                    isChecked={isChecked}
                    onCheck={handleCheck}
                    editMode={editMode}
                    isOwnVacation={vac.employee.id === currentUser?.id}
                  />
                )
              })
            )}
          </TableBody>
          <TableFooter
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: 'background.elevation1',
            }}
          >
            <TableRow>
              <StyledFooterCell colSpan={4} sx={{ p: 0 }}>
                <TablePagination
                  component="div"
                  count={data?.totalElements ?? 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) => {
                    return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
                  }}
                  sx={{ border: 'none', float: 'right' }}
                  slotProps={{
                    select: {
                      inputProps: {
                        id: 'data-table-pagination',
                      },
                      'aria-labelledby': 'data-table-pagination-label',
                    },
                  }}
                />
              </StyledFooterCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default UpcomingVacationsTable
